import { useEffect } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Spinner } from "shared";
import { FooterTable } from "shared/components/table/components/FooterTable";
import { formatCurrency } from "shared/utils";
import { useSeguimientoCuentas } from "../hooks/useSeguimientoCuentas";
import styles from "../styles/autorizationPayments.module.scss";

interface Props {
  open: boolean;
  onClose: () => void;
}

const TRANSACTION_TYPE_LABELS: Record<string, string> = {
  PROVISION: "Provisión de facturas",
  PROVISION_CREDIT_NOTE: "Reversión por nota crédito",
  PROVISION_DEBIT_NOTE: "Ajuste por nota débito",
};

export const ModalSeguimientoCuentas = ({ open, onClose }: Props) => {
  const {
    content,
    totalElements,
    numberPage,
    size,
    contentSize,
    isLoading,
    selectedRows,
    valuesResultsPages,
    fetchData,
    handleChangePage,
    handleChangeSizeRows,
    handleChangeCheck,
    handleSelectAll,
    clearSelection,
    onProvisionar,
  } = useSeguimientoCuentas();

  useEffect(() => {
    if (open) {
      fetchData(0, 20);
    } else {
      clearSelection();
    }
  }, [open]);

  const allSelected =
    content.length > 0 && selectedRows.length === content.length;
  const someSelected =
    selectedRows.length > 0 && selectedRows.length < content.length;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: "12px", overflow: "hidden" } }}
    >
      <DialogTitle sx={{ borderBottom: "1px solid rgba(0,0,0,0.06)", pb: 1.5 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              color:
                "rgba(var(--general-color-r), var(--general-color-g), var(--general-color-b), 1)",
              fontWeight: 600,
              fontSize: "1rem",
            }}
          >
            Seguimiento de cuentas por provisionar
          </Typography>
          <IconButton
            onClick={onClose}
            size="small"
            data-testid="btn-close-modal"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <TableContainer className={styles.containerTableResults}>
          <div className={styles.tableResults}>
            <Table stickyHeader>
              <TableHead>
                <TableRow className={styles.tablaHeader}>
                  <TableCell padding="checkbox" className={styles.headerCell}>
                    <Checkbox
                      checked={allSelected}
                      indeterminate={someSelected}
                      onChange={handleSelectAll}
                      size="small"
                    />
                  </TableCell>
                  <TableCell className={styles.headerCell}>
                    Número de factura
                  </TableCell>
                  <TableCell className={styles.headerCell}>Prestador</TableCell>
                  <TableCell className={styles.headerCell}>
                    Valor provisión
                  </TableCell>
                  <TableCell className={styles.headerCell}>
                    Tipo de transacción
                  </TableCell>
                  <TableCell className={styles.headerCell}>
                    Tipo de provisión
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {content.map((row, index) => {
                  const isSelected = selectedRows.some(
                    (r) =>
                      r.documentNumber === row.documentNumber &&
                      r.transactionType === row.transactionType &&
                      r.provisionType === row.provisionType,
                  );
                  return (
                    <TableRow
                      key={index}
                      selected={isSelected}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isSelected}
                          onChange={(e) => handleChangeCheck(e, row)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell className={styles.tableCell}>
                        {row.documentNumber}
                      </TableCell>
                      <TableCell className={styles.tableCell}>
                        {row.providerName}
                      </TableCell>
                      <TableCell className={styles.tableCell}>
                        {row.provisionValue != null
                          ? `${formatCurrency(row.provisionValue)}`
                          : "-"}
                      </TableCell>
                      <TableCell className={styles.tableCell}>
                        {row.transactionType
                          ? (TRANSACTION_TYPE_LABELS[row.transactionType] ??
                            row.transactionType)
                          : "-"}
                      </TableCell>
                      <TableCell className={styles.tableCell}>
                        {row.provisionType}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          <FooterTable
            sizeRows={String(size)}
            handleChangeSizePage={handleChangeSizeRows}
            valuesResultsPages={valuesResultsPages}
            numberPage={numberPage}
            totalElements={totalElements}
            size={size}
            handleChangePage={handleChangePage}
            contentSize={contentSize}
          />
        </TableContainer>
        {isLoading && <Spinner />}
      </DialogContent>
      <DialogActions
        sx={{
          px: 2.5,
          py: 1.5,
          borderTop: "1px solid rgba(0,0,0,0.06)",
          background: "linear-gradient(135deg, #ffffff 0%, #fafbfc 100%)",
          justifyContent: "flex-end",
          gap: 1,
        }}
      >
        {selectedRows.length > 0 && (
          <Typography
            sx={{ fontSize: "0.78rem", color: "#6d6d6d", mr: "auto" }}
          >
            {selectedRows.length}{" "}
            {selectedRows.length === 1
              ? "factura seleccionada"
              : "facturas seleccionadas"}
          </Typography>
        )}
        <Button
          className={styles.authorizationBtnFilters}
          onClick={onProvisionar}
          variant="contained"
          disabled={selectedRows.length === 0}
          data-testid="btn-actions-modal"
        >
          Provisionar
        </Button>
      </DialogActions>
    </Dialog>
  );
};
